<script>
import axios from "axios";
import Swal from "sweetalert2";
import { logActivityPeserta, getPesertaInfo } from "@/helpers/logService";

export default {
  name: "DetailMasterBlueprint",
  props: {
    idkompetensi: {
      type: [Number, String],
      required: true,
    },
    breadcrumbParent: {
      type: String,
      default: "Master Blueprint",
    },
    bannerTitle: {
      type: String,
      default: "Detail Master Blueprint",
    },
    showKembaliButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["back"],
  data() {
    return {
      currentIdKompetensi: this.idkompetensi,
      loading: true,
      detail: {
        kompetensi: {
          idkompetensi: null,
          nama_kompetensi: "-",
          kode_kompetensi: "-",
          nama_dahan: "-",
          nama_profesi: "-",
          definisi_kompetensi: "-",
          modul_pembelajaran: "-",
          referensi: "-",
          link_referensi: "-",
          level: null,
          nama_level: "-",
        },
        available_levels: [],
        levels: [],
      },
    };
  },
  watch: {
    idkompetensi(val) {
      this.currentIdKompetensi = val;
      this.fetchDetail();
    },
  },
  async mounted() {
    await this.fetchDetail();
  },
  methods: {
    async switchLevel(id) {
      if (this.currentIdKompetensi === id) return;
      this.currentIdKompetensi = id;
      await this.fetchDetail();
    },
    async fetchDetail() {
      this.loading = true;
      try {
        const token =
          localStorage.access_token ||
          localStorage.access_token_peserta ||
          localStorage.session;

        const baseUrl = process.env.VUE_APP_BACKEND_URL_VERSION;
        // Gunakan endpoint referensi jika user adalah peserta tanpa access_token staff/admin
        let url = `${baseUrl}master/kompetensi/${this.currentIdKompetensi}/blueprint-detail`;
        if (!localStorage.access_token && localStorage.access_token_peserta) {
          url = `${baseUrl}referensi/blueprint-detail/${this.currentIdKompetensi}`;
        }

        let response;
        try {
          response = await axios.get(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
        } catch (err) {
          // Fallback ke referensi jika endpoint master 403 / 401
          if (url.includes("master/kompetensi")) {
            url = `${baseUrl}referensi/blueprint-detail/${this.currentIdKompetensi}`;
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
          } else {
            throw err;
          }
        }

        if (response.data && response.data.data) {
          this.detail = response.data.data;
          // Log aktivitas peserta: Lihat detail blueprint
          logActivityPeserta("Master Blueprint", "Lihat detail blueprint", window.location.href);
        }
      } catch (error) {
        console.error("Gagal memuat detail blueprint:", error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text:
            error.response?.data?.message ||
            "Gagal memuat detail blueprint dari server.",
        });
      } finally {
        this.loading = false;
      }
    },
    formatUrl(url) {
      if (!url || url === "-") return "#";
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        return `https://${url}`;
      }
      return url;
    },
    formatJenisSoal(jenis) {
      if (!jenis) return "-";
      const jLower = jenis.toLowerCase();
      if (jLower.includes("pilihan ganda") || jLower === "pg") return "PG";
      if (jLower.includes("benar salah") || jLower === "bs") return "BS";
      if (jLower.includes("jodoh") || jLower.includes("menjodohkan")) return "Menjodohkan";
      if (jLower.includes("text") || jLower.includes("esai") || jLower.includes("essay")) return "Esai";
      return jenis;
    },
    async handleExportPdf() {
      if (!this.currentIdKompetensi) return;
      const peserta = getPesertaInfo();
      const params = new URLSearchParams();
      if (peserta.nip) params.append("nip", peserta.nip);
      if (peserta.nama) params.append("nama", peserta.nama);
      const queryString = params.toString() ? `?${params.toString()}` : "";

      const url = `${process.env.VUE_APP_BACKEND_URL_VERSION}export-pdf/blueprint-peserta/${this.currentIdKompetensi}${queryString}`;
      
      // Log aktivitas peserta: Download blueprint
      await logActivityPeserta("Master Blueprint", "Download blueprint", url);

      window.open(url, "_blank");
    },
  },
};
</script>

<template>
  <div class="blueprint-detail-wrapper">
    <!-- Breadcrumb -->
    <nav class="mb-3">
      <ol class="breadcrumb mb-0 py-1">
        <li class="breadcrumb-item">
          <a
            href="javascript:void(0)"
            class="text-decoration-none text-primary fw-medium"
            @click="$emit('back')"
          >
            <i class="fas fa-home me-1"></i> {{ breadcrumbParent }}
          </a>
        </li>
        <li class="breadcrumb-item active text-muted">Detail Blueprint</li>
      </ol>
    </nav>

    <!-- Banner Header -->
    <div class="blueprint-banner d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <div class="banner-icon-box me-3">
          <i class="fas fa-file-alt text-primary fa-lg"></i>
        </div>
        <div>
          <h4 class="mb-0 text-white fw-bold">{{ bannerTitle }}</h4>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <button
          type="button"
          class="btn btn-danger me-2 px-3 py-2 btn-export-pdf shadow-sm"
          @click="handleExportPdf"
        >
          <i class="fas fa-file-pdf me-2"></i> Export PDF
        </button>
        <button
          v-if="showKembaliButton"
          type="button"
          class="btn btn-white btn-kembali px-4 py-2"
          @click="$emit('back')"
        >
          <i class="fas fa-arrow-left me-2"></i> Kembali
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Memuat data detail master blueprint...</p>
    </div>

    <div v-else>
      <!-- CARD 1: INFORMASI KOMPETENSI -->
      <div class="card blueprint-card mb-4">
        <div class="card-header bg-transparent border-0 pt-4 pb-2 px-4">
          <div class="d-flex align-items-center">
            <div class="section-icon-box me-2">
              <i class="fas fa-file-alt text-primary"></i>
            </div>
            <h5 class="mb-0 fw-bold text-dark section-title">
              Informasi Kompetensi
            </h5>
          </div>
        </div>
        <div class="card-body px-4 pb-4 pt-2">
          <div class="row g-4">
            <!-- Kolom Kiri -->
            <div class="col-lg-6">
              <div class="info-list">
                <div class="info-item d-flex mb-3">
                  <div class="info-label text-uppercase fw-semibold text-muted">
                    Kompetensi
                  </div>
                  <div class="info-colon px-2">:</div>
                  <div class="info-value fw-medium text-dark flex-grow-1">
                    {{ detail.kompetensi.nama_kompetensi || "-" }}
                  </div>
                </div>

                <div class="info-item d-flex mb-3">
                  <div class="info-label text-uppercase fw-semibold text-muted">
                    Dahan Profesi
                  </div>
                  <div class="info-colon px-2">:</div>
                  <div class="info-value fw-medium text-dark flex-grow-1">
                    {{ detail.kompetensi.nama_dahan || "-" }}
                  </div>
                </div>

                <div class="info-item d-flex mb-3">
                  <div class="info-label text-uppercase fw-semibold text-muted">
                    Nama Profesi
                  </div>
                  <div class="info-colon px-2">:</div>
                  <div class="info-value fw-medium text-dark flex-grow-1">
                    {{ detail.kompetensi.nama_profesi || "-" }}
                  </div>
                </div>

                <div class="info-item d-flex mb-3">
                  <div class="info-label text-uppercase fw-semibold text-muted">
                    Kode Kompetensi
                  </div>
                  <div class="info-colon px-2">:</div>
                  <div class="info-value fw-medium text-dark flex-grow-1">
                    {{ detail.kompetensi.kode_kompetensi || "-" }}
                  </div>
                </div>

                <div class="info-item d-flex">
                  <div class="info-label text-uppercase fw-semibold text-muted">
                    Level Proficiency
                  </div>
                  <div class="info-colon px-2">:</div>
                  <div class="info-value fw-medium text-dark flex-grow-1">
                    <span class="badge badge-level-pill font-size-13 px-3 py-1 rounded-pill">
                      Level {{ detail.kompetensi.level || "-" }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Kolom Kanan -->
            <div class="col-lg-6">
              <div class="info-list">
                <div class="info-item d-flex">
                  <div class="info-label text-uppercase fw-semibold text-muted">
                    Definisi Kompetensi
                  </div>
                  <div class="info-colon px-2">:</div>
                  <div class="info-value text-dark flex-grow-1 text-justify lh-base">
                    {{ detail.kompetensi.definisi_kompetensi || "-" }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CARD 2: REFERENSI PEMBELAJARAN -->
      <div class="card blueprint-card mb-4">
        <div class="card-header bg-transparent border-0 pt-4 pb-2 px-4">
          <div class="d-flex align-items-center">
            <div class="section-icon-box me-2">
              <i class="fas fa-book-open text-primary"></i>
            </div>
            <h5 class="mb-0 fw-bold text-dark section-title">
              Referensi Pembelajaran
            </h5>
          </div>
        </div>
        <div class="card-body px-4 pb-4 pt-2">
          <div class="row g-3">
            <!-- Modul Pembelajaran -->
            <div class="col-lg-4">
              <div class="subcard-box h-100 p-3 rounded-3">
                <div class="subcard-label fw-bold text-uppercase text-muted mb-2 font-size-12">
                  Modul Pembelajaran
                </div>
                <div class="subcard-content text-dark fw-medium font-size-13">
                  {{ detail.kompetensi.modul_pembelajaran || "-" }}
                </div>
              </div>
            </div>

            <!-- Informasi Referensi -->
            <div class="col-lg-5">
              <div class="subcard-box h-100 p-3 rounded-3">
                <div class="subcard-label fw-bold text-uppercase text-muted mb-2 font-size-12">
                  Informasi Referensi
                </div>
                <div
                  class="subcard-content text-dark font-size-13 lh-base pre-line"
                >
                  {{ detail.kompetensi.referensi || "-" }}
                </div>
              </div>
            </div>

            <!-- Dokumen Referensi -->
            <div class="col-lg-3">
              <div class="subcard-box h-100 p-3 rounded-3">
                <div class="subcard-label fw-bold text-uppercase text-muted mb-2 font-size-12">
                  Dokumen Referensi
                </div>
                <div class="subcard-content font-size-13">
                  <a
                    v-if="detail.kompetensi.link_referensi && detail.kompetensi.link_referensi !== '-'"
                    :href="formatUrl(detail.kompetensi.link_referensi)"
                    target="_blank"
                    class="text-primary text-break text-decoration-none fw-medium d-inline-flex align-items-center"
                  >
                    <i class="fas fa-link me-1"></i>
                    <span>{{ detail.kompetensi.link_referensi }}</span>
                  </a>
                  <span v-else class="text-muted">-</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CARD 3: DETAIL LEVEL PROFICIENCY -->
      <div class="card blueprint-card mb-4">
        <div class="card-header bg-transparent border-0 pt-4 pb-2 px-4">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div class="d-flex align-items-center">
              <div class="section-icon-box me-2">
                <i class="fas fa-layer-group text-primary"></i>
              </div>
              <h5 class="mb-0 fw-bold text-dark section-title">
                Detail Level Proficiency
              </h5>
            </div>
            <!-- Level Switcher Buttons if sibling levels exist -->
            <div
              v-if="detail.available_levels && detail.available_levels.length > 1"
              class="d-flex align-items-center gap-1 bg-light p-1 rounded-pill border"
            >
              <span class="text-muted font-size-12 px-2 fw-medium">Pilih Level:</span>
              <button
                v-for="lvl in detail.available_levels"
                :key="lvl.idkompetensi"
                type="button"
                class="btn btn-sm rounded-pill px-3 py-1 fw-semibold transition-all"
                :class="lvl.idkompetensi === currentIdKompetensi ? 'btn-primary shadow-sm' : 'btn-light text-muted'"
                @click="switchLevel(lvl.idkompetensi)"
              >
                Level {{ lvl.level }}
              </button>
            </div>
          </div>
        </div>
        <div class="card-body px-4 pb-4 pt-2">
          <div class="table-responsive rounded-3 border">
            <table class="table table-bordered mb-0 proficiency-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 80px;">LEVEL</th>
                  <th style="width: 220px;">DESKRIPSI KOMPETENSI</th>
                  <th style="width: 220px;">ASSESSMENT OBJECTIVE</th>
                  <th style="min-width: 260px;">SCOPE OF ASESMEN</th>
                  <th class="text-center" style="width: 100px;">JENIS SOAL</th>
                  <th style="width: 180px;">TAKSONOMI BLOOM</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!detail.levels || detail.levels.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    Data proficiency belum tersedia untuk kompetensi ini.
                  </td>
                </tr>

                <tr v-else v-for="(row, idx) in detail.levels" :key="idx">
                  <!-- Level -->
                  <td class="text-center align-top pt-3">
                    <span class="badge badge-level-pill font-size-13 px-3 py-2 rounded-pill">
                      {{ row.level }}
                    </span>
                  </td>

                  <!-- Deskripsi Kompetensi -->
                  <td class="align-top font-size-13 text-dark lh-base">
                    {{ row.deskripsi_kompetensi || "-" }}
                  </td>

                  <!-- Assessment Objective -->
                  <td class="align-top font-size-13 text-dark lh-base">
                    {{ row.asesmen_objektif || "-" }}
                  </td>

                  <!-- Scope of Asesmen -->
                  <td class="align-top font-size-13 text-dark lh-base">
                    <div v-if="row.scopes && row.scopes.length > 0">
                      <div
                        v-for="(sc, sIdx) in row.scopes"
                        :key="sIdx"
                        class="mb-2 pb-2"
                        :class="{ 'border-bottom': sIdx < row.scopes.length - 1 }"
                      >
                        {{ sc }}
                      </div>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>

                  <!-- Jenis Soal -->
                  <td class="text-center align-top pt-3">
                    <div class="d-flex flex-wrap justify-content-center gap-1">
                      <span
                        v-for="(js, jIdx) in row.jenis_soal"
                        :key="jIdx"
                        class="badge bg-light-primary text-primary px-3 py-1 rounded-pill font-size-12 fw-semibold"
                      >
                        {{ formatJenisSoal(js) }}
                      </span>
                      <span
                        v-if="!row.jenis_soal || row.jenis_soal.length === 0"
                        class="text-muted"
                      >
                        -
                      </span>
                    </div>
                  </td>

                  <!-- Taksonomi Bloom -->
                  <td class="align-top font-size-13 text-dark lh-base">
                    <div v-if="row.taksonomi_bloom && row.taksonomi_bloom.length > 0">
                      <div
                        v-for="(tb, tIdx) in row.taksonomi_bloom"
                        :key="tIdx"
                        class="mb-1"
                      >
                        {{ tb }}
                      </div>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.blueprint-detail-wrapper {
  color: #334155;
}

/* Banner */
.blueprint-banner {
  background: linear-gradient(135deg, #1d72ec 0%, #3b82f6 50%, #60a5fa 100%);
  border-radius: 12px;
  padding: 22px 28px;
  box-shadow: 0 8px 20px rgba(29, 114, 236, 0.2);
}

.banner-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.btn-export-pdf {
  background-color: #ef4444;
  color: #ffffff;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  transition: all 0.2s ease;
}

.btn-export-pdf:hover {
  background-color: #dc2626;
  color: #ffffff;
  transform: translateY(-1px);
}

.btn-kembali {
  background-color: #ffffff;
  color: #1d72ec;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.btn-kembali:hover {
  background-color: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

/* Cards */
.blueprint-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}

.section-icon-box {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background-color: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-title {
  font-size: 16px;
  color: #0f172a;
}

/* Info List */
.info-label {
  width: 170px;
  min-width: 170px;
  font-size: 13px;
  letter-spacing: 0.3px;
}

.info-colon {
  font-weight: 600;
  color: #94a3b8;
}

.info-value {
  font-size: 13px;
  line-height: 1.6;
}

/* Subcard Boxes */
.subcard-box {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.font-size-12 {
  font-size: 12px;
}

.font-size-13 {
  font-size: 13px;
}

.pre-line {
  white-space: pre-line;
}

/* Proficiency Table */
.proficiency-table thead tr th {
  background-color: #0f3b7d !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.5px;
  border-color: #0f3b7d !important;
  padding: 14px 12px;
  vertical-align: middle;
}

.proficiency-table tbody td {
  padding: 14px 14px;
  border-color: #e2e8f0;
  vertical-align: top;
}

.badge-level-pill {
  background-color: #e0f2fe;
  color: #0284c7;
  font-weight: 700;
  min-width: 36px;
  display: inline-block;
}

.bg-light-primary {
  background-color: #eff6ff !important;
  color: #1d72ec !important;
  border: 1px solid #bfdbfe;
}

.transition-all {
  transition: all 0.2s ease;
}
</style>
